import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Typography from "@mui/material/Typography";
import {LogoutOutlined, Search, VisibilityOff, Telegram, HeadsetMic} from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import {MainListItems} from "./Menu";
import * as React from "react";
import useRootHook from "../hooks/useRoot";
import Dashboard from "./Dashboard";
import {Route, Routes, useLocation, useNavigate} from "react-router-dom";
import { Outlet } from "react-router-dom";
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import TransactionContainer from "./transactions/TransactionContainer";
import {RequireAuth} from './ACL'
import AppBar from './AppBar'
import Drawer from './Drawer'
import StatsIndex from "./stats/StatsIndex";
import ConfirmDialog from "./ConfirmDialog";
import helpers from "../helpers/common";
import CustomerAdd from "./customers/CustomerAdd";
import CustomerIndex from "./customers/CustomerIndex";
import CustomerEdit from "./customers/CustomerEdit";
import CurrencyIndex from "./currencies/CurrencyIndex";
import CurrencyEdit from "./currencies/CurrencyEdit";
import CurrencyAdd from "./currencies/CurrencyAdd";
import ServerIndex from "./servers/ServerIndex";
import ServerEdit from "./servers/ServerEdit";
import ServerAdd from "./servers/ServerAdd";
import PayMethodIndex from "./paymethods/PayMethodIndex";
import PayMethodEdit from "./paymethods/PayMethodEdit";
import PayMethodAdd from "./paymethods/PayMethodAdd";
import Traffic from "./atlas/Traffic";
import LimitsIndex from "./limits/LimitsIndex";
import PaymentDomains from "./settings/PaymentDomains";
import merchants from "../models/merchants";
import admins from "../models/admins";
import referrals from "../models/referrals";
import AdminIndex from "./admins/AdminIndex";
import AdminAdd from "./admins/AdminAdd";
import AdminEdit from "./admins/AdminEdit";
import Clock from "./Clock";
import MerchantIndex from "./merchants/MerchantIndex";
import MerchantEdit from "./merchants/MerchantEdit";
import MerchantAdd from "./merchants/MerchantAdd";
import ProjectIndex from "./projects/ProjectIndex";
import ProjectEdit from "./projects/ProjectEdit";
import ProjectAdd from "./projects/ProjectAdd";
import {Alert, Chip, InputAdornment, TextField, Tooltip, Popover} from "@mui/material";
import PayoutIndex from "./payouts/PayoutIndex";
import PayoutEdit from "./payouts/PayoutEdit";
import PayoutAdd from "./payouts/PayoutAdd";
import PayoutMethodIndex from "./payout-methods/PayoutMethodIndex";
import PayoutMethodEdit from "./payout-methods/PayoutMethodEdit";
import PayoutMethodAdd from "./payout-methods/PayoutMethodAdd";
import ActionLogsContainer from "./action-logs/ActionLogsContainer";
import PaymentPageOpensContainer from "./payment-page-opens/PaymentPageOpensContainer";
import ReferralIndex from "./referrals/ReferralIndex";
import ReferralAdd from "./referrals/ReferralAdd";
import ReferralEdit from "./referrals/ReferralEdit";
import ReferralStats from "./referrals/ReferralStats";
import NewsIndex from "./news/NewsIndex";
import NewsAdd from "./news/NewsAdd";
import NewsEdit from "./news/NewsEdit";
import TicketIndex from "./tickets/TicketIndex";
import TicketView from "./tickets/TicketView";

function PageGrid() {
    return (
        <Grid container spacing={{ xs: 2, md: 3 }}>
            <Outlet />
        </Grid>
    )
}

export default function Main() {
    const {setAuthed, userData, setUserData, setShowLoader, forcedMerchantID, removeForcedMerchant, showConfirm} = useRootHook();
    const [open, setOpen] = React.useState(true)
    const [confirmOpen, setConfirmOpen] = React.useState(false)
    const [supportAnchor, setSupportAnchor] = React.useState(null)
    const supportOpen = Boolean(supportAnchor)
    const [supportNow, setSupportNow] = React.useState(() => new Date())
    React.useEffect(() => {
        if (!supportOpen) return
        const t = setInterval(() => setSupportNow(new Date()), 60000)
        return () => clearInterval(t)
    }, [supportOpen])
    const gmt1Hour = (supportNow.getUTCHours() + 1 + 24) % 24
    const supportOnline = gmt1Hour >= 9 && gmt1Hour < 19
    const navigate = useNavigate()
    const location = useLocation()

    const toggleDrawer = () => {
        setOpen(!open)
    }
    const confirmClearMerchant = async () => {
        const confirmed = await showConfirm('Clear merchant?', 'Clear Merchant')
        if (confirmed) {
            removeForcedMerchant()
            const oldPath = location.pathname
            navigate('/')
            oldPath === '/' && window.location.reload()
        }
    }

    const MerchantIDInfo = () => {
        if (!forcedMerchantID) return ''

        return <Chip label={`MID ${forcedMerchantID}`} deleteIcon={<VisibilityOff />} color="primary" onDelete={confirmClearMerchant} />
    }

    const handleLogout = () => {
        setShowLoader(true)
        let logoutModel;
        if (userData.role === helpers.roles.M) {
            logoutModel = merchants;
        } else if (userData.role === helpers.roles.R) {
            logoutModel = referrals;
        } else {
            logoutModel = admins;
        }
        logoutModel.logout().then(() => {
            setShowLoader(false)
            setAuthed(false)
            setUserData(false)
        })
    }

    return (
        <React.Fragment>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />
                <AppBar
                    position="fixed"
                    open={open}
                    color="transparent"
                    elevation={0}
                    sx={{
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                    }}
                >
                    <Toolbar
                        sx={{
                            pr: 2,
                            gap: 1.5,
                        }}
                    >
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={toggleDrawer}
                            sx={{
                                ...(open && { display: 'none' }),
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography component="div" sx={{ fontWeight: 800, mr: 1 }}>
                            Atlas24
                        </Typography>
                        <MerchantIDInfo />

                        <TextField
                            placeholder="Search…"
                            size="small"
                            sx={{
                                flexGrow: 1,
                                maxWidth: 560,
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'background.default',
                                    borderRadius: 999,
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                <Clock />
                            </Box>
                            <Tooltip title="Telegram Channel">
                                <IconButton 
                                    color="inherit" 
                                    component="a"
                                    href="https://t.me/+MJvDUVLR_U1iMWIy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Telegram />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={supportOnline ? 'Support • Online (GMT+1 09:00–19:00)' : 'Support • Offline (GMT+1 09:00–19:00)'}>
                                <IconButton
                                    color="inherit"
                                    onClick={(e) => setSupportAnchor(e.currentTarget)}
                                    aria-label="Support"
                                >
                                    <HeadsetMic />
                                </IconButton>
                            </Tooltip>
                            <Popover
                                open={supportOpen}
                                anchorEl={supportAnchor}
                                onClose={() => setSupportAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <Typography sx={{ p: 2, minWidth: 220 }}>
                                    <strong>Support</strong><br />
                                    {supportOnline ? (
                                        <Chip label="Online" color="success" size="small" sx={{ mt: 0.5 }} />
                                    ) : (
                                        <Chip label="Offline" size="small" sx={{ mt: 0.5 }} />
                                    )}
                                    <br />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        GMT+1: 09:00 – 19:00
                                    </Typography>
                                </Typography>
                            </Popover>
                            <Tooltip title="Logout">
                                <IconButton color="inherit" onClick={() => setConfirmOpen(true)}>
                                    <LogoutOutlined />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Drawer variant="permanent" open={open}>
                    <Toolbar
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            px: [1],
                        }}
                    >
                        <IconButton onClick={toggleDrawer} aria-label="collapse sidebar">
                            <ChevronLeftIcon />
                        </IconButton>
                    </Toolbar>
                    <Divider />
                    <List component="nav">
                        <MainListItems />
                        <Divider sx={{ my: 1 }} />
                        {/*{userData.role === helpers.roles.M && <SecondaryListItems />}*/}
                    </List>
                </Drawer>
                <Box
                    component="main"
                    sx={{
                        backgroundColor: 'background.default',
                        flexGrow: 1,
                        minHeight: '100vh',
                        overflow: 'auto',
                    }}
                >
                    <Toolbar />
                    {((userData?.role === helpers.roles.M && userData?.can_create_payments !== true) || userData?.viewAsMerchant?.can_create_payments === false) && (
                        <Alert severity="warning" sx={{ mx: 0, mt: 0, borderRadius: 0, py: 1.5, '& .MuiAlert-message': { width: '100%' } }} variant="filled">
                            At the moment, the creation of new payments is temporarily unavailable. Our team is actively working to resolve the issue as quickly as possible. We apologize for any inconvenience this may cause and appreciate your understanding.
                        </Alert>
                    )}
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={{
                            mt: 3,
                            mb: 6,
                            px: { xs: 2, md: 3 },
                            // Stripe-like centered content width (wider than before, avoids "tiny card + huge empty space")
                            maxWidth: 1440,
                            mx: 'auto',
                        }}
                    >
                            <Routes>
                                <Route path="/*" element={<RequireAuth />}>
                                <Route element={<PageGrid />}>
                                    <Route index element={<Dashboard />} />
                                    <Route path="stats" element={<StatsIndex />} />
                                    <Route path="action-logs" element={<RequireAuth forceRoles={[helpers.roles.A, helpers.roles.S]} />}>
                                        <Route index element={<ActionLogsContainer />} />
                                    </Route>
                                    <Route path="payment-page-opens" element={<RequireAuth forceRoles={[helpers.roles.A, helpers.roles.S]} />}>
                                        <Route index element={<PaymentPageOpensContainer />} />
                                    </Route>
                                    <Route path="atlas/traffic" element={<Traffic />} />
                                    <Route path="limits" element={<RequireAuth forceRoles={[helpers.roles.A, helpers.roles.S]} />}>
                                        <Route index element={<LimitsIndex />} />
                                    </Route>
                                    <Route path="merchants">
                                        <Route index element={<MerchantIndex />} />
                                        <Route path="edit" element={<RequireAuth />}>
                                            <Route index element={<MerchantEdit />} />
                                        </Route>
                                        <Route path="add" element={<RequireAuth />}>
                                            <Route index element={<MerchantAdd />} />
                                        </Route>
                                    </Route>
                                    <Route path="projects">
                                        <Route index element={<ProjectIndex />} />
                                        <Route path="edit" element={<RequireAuth />}>
                                            <Route index element={<ProjectEdit />} />
                                        </Route>
                                        <Route path="add" element={<RequireAuth />}>
                                            <Route index element={<ProjectAdd />} />
                                        </Route>
                                    </Route>
                                    <Route path="transactions" element={<RequireAuth />} >
                                        <Route index element={<TransactionContainer />} />
                                    </Route>
                                    <Route path="customers">
                                        <Route index element={<CustomerIndex />} />
                                        <Route path="edit" element={<RequireAuth />}>
                                            <Route index element={<CustomerEdit />} />
                                        </Route>
                                        <Route path="add" element={<RequireAuth />}>
                                            <Route index element={<CustomerAdd />} />
                                        </Route>
                                    </Route>
                                    <Route path="currencies" element={<RequireAuth forceRoles={[helpers.roles.S, helpers.roles.A]}/>} >
                                        <Route index element={<CurrencyIndex />} />
                                        <Route path="edit" element={<CurrencyEdit />} />
                                        <Route path="add" element={<CurrencyAdd />}/>
                                    </Route>
                                    <Route path="paymethods">
                                        <Route index element={<PayMethodIndex />} />
                                        <Route path="edit" element={<RequireAuth />}>
                                            <Route index element={<PayMethodEdit />} />
                                        </Route>
                                        <Route path="add" element={<RequireAuth />}>
                                            <Route index element={<PayMethodAdd />} />
                                        </Route>
                                    </Route>
                                    <Route path="payment-domains" element={<RequireAuth forceRoles={[helpers.roles.S, helpers.roles.A]} />}>
                                        <Route index element={<PaymentDomains />} />
                                    </Route>
                                    <Route path="payouts">
                                        <Route index element={<PayoutIndex />} />
                                        <Route path="edit" element={<RequireAuth />}>
                                            <Route index element={<PayoutEdit />} />
                                        </Route>
                                        <Route path="add" element={<RequireAuth />}>
                                            <Route index element={<PayoutAdd />} />
                                        </Route>
                                    </Route>
                                    <Route path="payout-methods">
                                        <Route index element={<PayoutMethodIndex />} />
                                        <Route path="edit" element={<RequireAuth />}>
                                            <Route index element={<PayoutMethodEdit />} />
                                        </Route>
                                        <Route path="add" element={<RequireAuth />}>
                                            <Route index element={<PayoutMethodAdd />} />
                                        </Route>
                                    </Route>
                                    <Route path="servers" element={<RequireAuth />} >
                                        <Route index element={<ServerIndex />} />
                                        <Route path="edit" element={<ServerEdit />} />
                                        <Route path="add" element={<ServerAdd />}/>
                                    </Route>
                                    <Route path="admins" element={<RequireAuth />} >
                                        <Route index element={<AdminIndex />} />
                                        <Route path="edit" element={<AdminEdit />} />
                                        <Route path="add" element={<AdminAdd />}/>
                                    </Route>
                                    <Route path="referrals" element={<RequireAuth forceRoles={[helpers.roles.A, helpers.roles.S]} />}>
                                        <Route index element={<ReferralIndex />} />
                                        <Route path="edit" element={<ReferralEdit />} />
                                        <Route path="add" element={<ReferralAdd />} />
                                    </Route>
                                    <Route path="referrals/stats" element={<RequireAuth forceRoles={[helpers.roles.R]} />}>
                                        <Route index element={<ReferralStats />} />
                                    </Route>
                                    <Route path="news" element={<RequireAuth forceRoles={[helpers.roles.A, helpers.roles.S]} />}>
                                        <Route index element={<NewsIndex />} />
                                        <Route path="edit" element={<NewsEdit />} />
                                        <Route path="add" element={<NewsAdd />}/>
                                    </Route>
                                    <Route path="tickets" element={<RequireAuth />}>
                                        <Route index element={<TicketIndex />} />
                                        <Route path=":id" element={<TicketView />} />
                                    </Route>
                                </Route>
                                </Route>
                            </Routes>
                    </Container>
                </Box>
            </Box>
            <ConfirmDialog title="Please confirm you want to logout" confirmOpen={confirmOpen} setConfirmOpen={setConfirmOpen} onConfirm={handleLogout} />
        </React.Fragment>
    );
}

//todo remove M role from currencies